import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Header from "@/components/layout/header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  AlertCircle,
  MapPin,
  Globe,
  ChevronRight,
} from "lucide-react";
import {
  usePerkLocations,
  useCreatePerkLocation,
  useUpdatePerkLocation,
  useDeletePerkLocation,
} from "@/hooks/usePerkLocations";
import type { PerkLocationResponse, PerkLocationRequest, PerkLocationType } from "@/types/generated/cms.generated";
import { cn } from "@/lib/utils";

const locationFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  locationType: z.enum(["Country", "Region", "Online"]),
  countryCode: z.string().optional(),
  parentId: z.string().optional(),
  sortOrder: z.number().default(0),
});

type LocationFormData = z.infer<typeof locationFormSchema>;

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  location?: PerkLocationResponse;
  parentLocations: PerkLocationResponse[];
}

function LocationModal({ open, onClose, location, parentLocations }: LocationModalProps) {
  const createLocation = useCreatePerkLocation();
  const updateLocation = useUpdatePerkLocation();

  const form = useForm<LocationFormData>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      name: "",
      locationType: "Country",
      countryCode: "",
      parentId: "",
      sortOrder: 0,
    },
  });

  const locationType = form.watch("locationType");

  useEffect(() => {
    if (open) {
      form.reset({
        name: location?.name || "",
        locationType: location?.locationType || "Country",
        countryCode: location?.countryCode || "",
        parentId: location?.parentId || "",
        sortOrder: location?.sortOrder || 0,
      });
    }
  }, [location, open, form]);

  const onSubmit = async (data: LocationFormData) => {
    const payload: PerkLocationRequest = {
      name: data.name,
      locationType: data.locationType,
      countryCode: data.locationType === "Country" ? data.countryCode || undefined : undefined,
      parentId: data.locationType === "Region" ? data.parentId || undefined : undefined,
      sortOrder: data.sortOrder,
    };

    try {
      if (location?.id) {
        await updateLocation.mutateAsync({ id: location.id, data: payload });
      } else {
        await createLocation.mutateAsync(payload);
      }
      form.reset();
      onClose();
    } catch {
      // Error handled by hooks
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{location ? "Edit Location" : "Create Location"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="locationType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Country">Country</SelectItem>
                      <SelectItem value="Region">Region</SelectItem>
                      <SelectItem value="Online">Online</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={
                        locationType === "Country"
                          ? "e.g. United Arab Emirates"
                          : locationType === "Region"
                            ? "e.g. Dubai"
                            : "e.g. Worldwide Online"
                      }
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {locationType === "Country" && (
              <FormField
                control={form.control}
                name="countryCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ISO Alpha-3 Country Code</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. ARE" maxLength={3} {...field} />
                    </FormControl>
                    <FormDescription>ISO 3166-1 alpha-3 code (3 letters)</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {locationType === "Region" && (
              <FormField
                control={form.control}
                name="parentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parent Country</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ""}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select parent country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {parentLocations
                          .filter((l) => l.locationType === "Country")
                          .map((l) => (
                            <SelectItem key={l.id} value={l.id || ""}>
                              {l.name} {l.countryCode ? `(${l.countryCode})` : ""}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createLocation.isPending || updateLocation.isPending}
              >
                {location ? "Update" : "Create"} Location
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// Tree node component for hierarchical display
interface LocationTreeNodeProps {
  location: PerkLocationResponse;
  depth: number;
  onEdit: (location: PerkLocationResponse) => void;
  onDelete: (id: string) => void;
}

function LocationTreeNode({ location, depth, onEdit, onDelete }: LocationTreeNodeProps) {
  const typeIcon: Record<PerkLocationType, React.ReactNode> = {
    Country: <MapPin className="w-4 h-4 text-blue-500" />,
    Region: <ChevronRight className="w-4 h-4 text-muted-foreground" />,
    Online: <Globe className="w-4 h-4 text-green-500" />,
  };

  return (
    <>
      <div
        className={cn(
          "flex items-center justify-between py-3 px-4 border-b hover:bg-muted/50 transition-colors",
          depth > 0 && "bg-muted/20"
        )}
        style={{ paddingLeft: `${16 + depth * 24}px` }}
      >
        <div className="flex items-center gap-3">
          {typeIcon[location.locationType || "Country"]}
          <div>
            <span className="font-medium">{location.name}</span>
            {location.countryCode && (
              <Badge variant="outline" className="ml-2 text-xs">
                {location.countryCode}
              </Badge>
            )}
          </div>
          <Badge variant="secondary" className="text-xs">
            {location.locationType}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(location)}>
            <Edit className="w-4 h-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Location</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{location.name}"?
                  {location.children && location.children.length > 0 &&
                    " This will also remove its child regions."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => location.id && onDelete(location.id)}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      {location.children?.map((child) => (
        <LocationTreeNode
          key={child.id}
          location={child}
          depth={depth + 1}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </>
  );
}

export default function PerkLocationsPage() {
  const { data: locations = [], isLoading, error } = usePerkLocations();
  const deleteLocation = useDeletePerkLocation();

  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<PerkLocationResponse | undefined>();

  // Flatten for search filtering (but display hierarchically)
  const filteredLocations = useMemo(() => {
    if (!searchQuery) return locations;
    const query = searchQuery.toLowerCase();

    // Filter: show parents whose name or children names match
    return locations.filter((loc) => {
      const nameMatch = loc.name?.toLowerCase().includes(query);
      const codeMatch = loc.countryCode?.toLowerCase().includes(query);
      const childMatch = loc.children?.some(
        (c) => c.name?.toLowerCase().includes(query) || c.countryCode?.toLowerCase().includes(query)
      );
      return nameMatch || codeMatch || childMatch;
    });
  }, [locations, searchQuery]);

  // Get all top-level locations for parent selection
  const topLevelLocations = useMemo(() => {
    return locations.filter((l) => l.locationType === "Country" || !l.parentId);
  }, [locations]);

  const totalCount = useMemo(() => {
    let count = 0;
    locations.forEach((l) => {
      count++;
      if (l.children) count += l.children.length;
    });
    return count;
  }, [locations]);

  const handleEdit = (location: PerkLocationResponse) => {
    setEditingLocation(location);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingLocation(undefined);
  };

  const handleDelete = (id: string) => {
    deleteLocation.mutate(id);
  };

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Perk Locations" description="Manage perk locations" />
        <div className="flex-1 p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error loading locations</AlertTitle>
            <AlertDescription>Failed to load perk locations.</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header title="Perk Locations" description="Manage hierarchical locations for perks (Country, Region, Online)">
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Location
        </Button>
      </Header>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search locations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Badge variant="outline">{totalCount} locations</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Tree */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Location Hierarchy
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filteredLocations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {searchQuery ? "No locations match your search." : "No locations created yet."}
              </div>
            ) : (
              filteredLocations.map((location) => (
                <LocationTreeNode
                  key={location.id}
                  location={location}
                  depth={0}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <LocationModal
        open={modalOpen}
        onClose={handleCloseModal}
        location={editingLocation}
        parentLocations={topLevelLocations}
      />
    </div>
  );
}
